import {
  toWSDateString,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSSidebar,
  WSText
} from "@wingspanhq/fe-component-library";
import { IPayableSchema } from "@wingspanhq/payments/dist/interfaces";
import {
  BillingFeeItemType,
  Currency,
  PayableStatus,
  PayeeReviewStatus
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { WSQueryConfig } from "@ws-react-query";
import sortBy from "lodash/sortBy";
import React, { useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Card } from "../../../ClientPayments/components/Card/Card";
import { queriesPayables } from "@query";
import { useXWingspanPayerId } from "../../../modules/Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { selectorPayeeDisplayName } from "../../../shared/selectors/selectorPayeeDisplayName";
import { selectorPayerDisplayName } from "../../../shared/selectors/selectorPayerDisplayName";
import { WSQueries } from "../../../query/WSQuery";
import { useWSQuery } from "../../../query/helpers";
import { QUERY_PAYABLE } from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { getParentPath } from "../../../utils/goToParentRoute";
import { PayableActivity } from "../../components/PayableActivity";
import { PayableDetailsHeader } from "../../components/PayableDetailsHeader";
import { PayableParentInvoice } from "../../components/PayableParentInvoice";
import { PayableWorkLog } from "../../components/PayableWorkLog";
import { CancelledPayableWorkLogInfo } from "../../../modules/WorkLogs/components/CancelledPayableWorkLogInfo";
import { QuickbooksHistory } from "../../components/QuickbooksHistory/QuickbooksHistory";
import { QuickbooksWrapper } from "../../components/QuickbooksWrapper/QuickbooksWrapper";
import { Totals, TotalsSection } from "../../components/Totals/Totals";
import styles from "../InvoicesInvoiceDetails.module.scss";

// v1 payable read — kept for the still-v1 consumers (EditPayable, DeductionsHistoryList,
// WorkLogs). The detail screen below reads via the v3 registry.
export const usePayableQuery = (
  payableId: string,
  config?: WSQueryConfig<IPayableSchema>
) =>
  useWSQuery<IPayableSchema>(
    [QUERY_PAYABLE, payableId],
    () => paymentsService.payable.get(payableId),
    {
      ...config
    }
  );

type Props = RouteComponentProps<
  { payableId: string; year: string },
  {},
  { backPath?: string }
>;

export const PayableDetails: React.FC<Props> = ({
  match,
  history,
  location
}) => {
  const backPath = location.state?.backPath || getParentPath(history);
  const onBack = () => {
    if (backPath) {
      history.push({
        pathname: backPath,
        search: location.search,
        state: {
          ...(location.state ?? {})
        }
      });
    }
  };

  return (
    <WSSidebar.Container onClose={onBack}>
      <Inner payableId={match.params.payableId} />
    </WSSidebar.Container>
  );
};

type InnerProps = {
  payableId: string;
};

const Inner: React.FC<InnerProps> = ({ payableId }) => {
  const [detailsVisible, setDetailsVisible] = useState(true);

  const xWingspanAccount = useXWingspanPayerId();
  const queryPayable = queriesPayables.getPayable.useQuery({
    payableId,
    xWingspanAccount
  });

  return (
    <WSQueries queries={{ queryPayable }}>
      {({ queryPayable: { data: payable } }) => {
        const payee = payable.payee!;
        const payer = payable.payer!;
        const currency = (payable.currency || "USD") as Currency;

        const payableLineItems = sortBy(payable.lineItems, lineItem =>
          lineItem.metadata?.bulkImporterItemNumber
            ? Number(lineItem.metadata.bulkImporterItemNumber)
            : undefined
        );

        const amount = payable.amount ?? 0;
        const deductionsTotal = payable.amountDetails?.deductions ?? 0;
        const lateFeeTotal = (payable.fees?.items ?? [])
          .filter(item => item.type === BillingFeeItemType.LATE)
          .reduce((sum, item) => sum + (item.amount ?? 0), 0);

        const totalsSections: TotalsSection[] = [
          {
            items: payableLineItems.map(lineItem => [
              lineItem.description || "",
              formatMoney(lineItem.totalCost ?? 0, currency)
            ])
          },
          {
            items: [
              {
                bold: true,
                left: "Total",
                right: formatMoney(amount, currency)
              }
            ]
          }
        ];

        if (deductionsTotal > 0) {
          totalsSections.push({
            items: [
              {
                left: "Deductions",
                right: formatMoney(-deductionsTotal, currency)
              },
              {
                bold: true,
                left: "Contractor gross income",
                right: formatMoney(amount - deductionsTotal, currency)
              }
            ]
          });
        }

        const isDisputed =
          payable.payeeReviewStatus === PayeeReviewStatus.DISPUTED;
        const metadata = payable.metadata ?? {};

        return (
          <WSSidebar.Layout
            header={
              payable.externalId ? `Invoice #${payable.externalId}` : "Invoice"
            }
          >
            <WSElement className={styles.main}>
              <Card mb="XL">
                <WSText.Heading4 mb="M">
                  {selectorPayeeDisplayName(payee)}
                </WSText.Heading4>

                {payable.status === PayableStatus.CANCELLED && (
                  <CancelledPayableWorkLogInfo payable={payable} />
                )}

                <PayableDetailsHeader payable={payable} payee={payee} />

                {payable.status === PayableStatus.PENDING ? (
                  <WSAlert
                    theme="warning"
                    icon="alert-circle"
                    title="Not yet eligible for payments"
                    mt="XL"
                  >
                    Your invite was sent. We're still waiting for this person to
                    complete their onboarding requirements before they can
                    receive payments.
                  </WSAlert>
                ) : null}

                {isDisputed && (
                  <WSAlert
                    theme="info"
                    icon="info-circle"
                    title="About disputed invoices"
                    mt="XL"
                  >
                    A contractor has disputed this invoice. Review their reason
                    in "All Activity," then approve, edit, or cancel the
                    invoice. As the payer, you have final approval.
                  </WSAlert>
                )}
              </Card>

              <Card mb="XL">
                <WSFlexBox wrap="nowrap" justify="space-between">
                  <WSButton.Link
                    onClick={() => {
                      setDetailsVisible(!detailsVisible);
                    }}
                    rightIcon={detailsVisible ? "caret-up" : "caret-down"}
                  >
                    {detailsVisible ? "Hide" : "Show"} invoice details
                  </WSButton.Link>
                  <WSText.Heading5>
                    {formatMoney(amount, currency)}
                  </WSText.Heading5>
                </WSFlexBox>
                {detailsVisible && (
                  <WSElement mt="XL">
                    <WSElement mb="XL">
                      <WSText.ParagraphSm mb="XS" color="gray500">
                        Bill to
                      </WSText.ParagraphSm>
                      <WSText mb="XL">{selectorPayerDisplayName(payer)}</WSText>

                      <WSText.ParagraphSm mb="XS" color="gray500">
                        From
                      </WSText.ParagraphSm>
                      <WSText>{selectorPayeeDisplayName(payee)}</WSText>
                    </WSElement>

                    <WSElement mb="XL">
                      <WSText.ParagraphSm mb="XS" color="gray500">
                        Due date
                        {!!payable.scheduledPaymentDate && " (updated)"}
                      </WSText.ParagraphSm>
                      <WSText>
                        {toWSDateString(
                          payable.scheduledPaymentDate || payable.dueDate,
                          "monthDayYear"
                        )}
                      </WSText>
                    </WSElement>
                    {metadata.purchaseOrderNumber ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          PO Number
                        </WSText.ParagraphSm>
                        <WSText>{metadata.purchaseOrderNumber}</WSText>
                      </WSElement>
                    ) : null}
                    {metadata.projectName ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Project Name
                        </WSText.ParagraphSm>
                        <WSText>{metadata.projectName}</WSText>
                      </WSElement>
                    ) : null}
                    {payable.notes ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Notes
                        </WSText.ParagraphSm>
                        <WSText>{payable.notes}</WSText>
                      </WSElement>
                    ) : null}
                    {lateFeeTotal > 0 ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Late fee
                        </WSText.ParagraphSm>
                        <WSText>{formatMoney(lateFeeTotal, currency)}</WSText>
                      </WSElement>
                    ) : null}

                    <WSElement mb="XL">
                      <PayableWorkLog payable={payable} />
                    </WSElement>

                    <Totals sections={totalsSections} />
                  </WSElement>
                )}

                {payable.parentInvoiceId && (
                  <WSElement mt="2XL">
                    <PayableParentInvoice
                      parentInvoiceId={payable.parentInvoiceId}
                    />
                  </WSElement>
                )}
              </Card>

              <Card>
                <WSText.Heading5 mb="XL">All activity</WSText.Heading5>
                <PayableActivity payable={payable} />
              </Card>
              <QuickbooksWrapper>
                <QuickbooksHistory
                  Wrapper={Card}
                  mt="XL"
                  entityId={payable.id!}
                />
              </QuickbooksWrapper>
            </WSElement>
          </WSSidebar.Layout>
        );
      }}
    </WSQueries>
  );
};
